import { Suspense, lazy, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Hand, Mic, MicOff } from "lucide-react";

import type { AnimalId } from "@/components/camp/campData";
import type { StagePerson } from "@/components/camp/FireStage";
import { useHydrated } from "@/hooks/useHydrated";
import { cn } from "@/engine/support/classNames";
import { NightStage } from "@/components/camp/NightStage";
import { usePrefersReducedMotion } from "@/hooks/usePrefersReducedMotion";

import { NightScope } from "./NightScope";

/**
 * A room, drawn: its code, seven people around the fire, and its bar (#942).
 *
 * Nothing in it is connected. It is the product's picture of itself with the
 * controls left working — a visitor walks round the fire, mutes themselves,
 * raises a hand, throws a reaction — so the first screen answers "what is
 * this" by being used rather than read.
 *
 * It is drawn twice over. The server paints the application's `NightStage`, so
 * the first frame already shows people at a fire; once the page is live and
 * the browser is idle, the room's own renderer is fetched (`ShowcaseScene`) and
 * takes over the same box — the forest, the seats and the walk a room has.
 * The renderer is part of the room, which the landing must not carry before
 * first paint, and somebody who asked for no motion keeps the still picture.
 */
const ShowcaseScene = lazy(() => import("@/components/room/ShowcaseScene"));

interface Seat {
  id: string;
  beast: AnimalId;
  /** Where `NightStage` stands them, and where the room's ring does. */
  seat: number;
  spot: number;
}

const YOU: Seat = { id: "you", beast: "hare", seat: 1, spot: 0 };
const OTHERS: readonly Seat[] = [
  { id: "kate", beast: "fox", seat: 3, spot: 2 },
  { id: "sasha", beast: "owl", seat: 5, spot: 4 },
  { id: "anna", beast: "deer", seat: 6, spot: 6 },
  { id: "dima", beast: "bear", seat: 8, spot: 8 },
  { id: "leo", beast: "raccoon", seat: 10, spot: 9 },
  { id: "misha", beast: "turtle", seat: 11, spot: 11 },
];

/** Misha keeps their microphone off, so the picture shows what that looks like. */
const MUTED = "misha";
const TALKERS = OTHERS.filter(({ id }) => id !== MUTED).map(({ id }) => id);
const LINES = ["where", "code", "again", "fire", "hear"] as const;
const REACTIONS = ["🔥", "👏", "😂"] as const;

/** Long enough to read as a turn in a conversation rather than a blink. */
const TURN_MS = 2600;
const REACTION_MS = 1800;

/** One person speaks at a time, and now and then says something. */
function useTurns(still: boolean) {
  const [turn, setTurn] = useState(1);
  useEffect(() => {
    if (still) return;
    const timer = setInterval(() => setTurn((now) => now + 1), TURN_MS);
    return () => clearInterval(timer);
  }, [still]);
  const speaking = TALKERS[turn % TALKERS.length] ?? "kate";
  const line = {
    peerId: speaking,
    key: turn,
    said: LINES[turn % LINES.length] ?? "where",
  };
  return { speaking, line };
}

function useReaction() {
  const [reaction, setReaction] = useState<{
    emoji: string;
    key: number;
  } | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);
  useEffect(() => () => clearTimeout(timer.current), []);
  const react = (emoji: string) => {
    clearTimeout(timer.current);
    setReaction((now) => ({ emoji, key: (now?.key ?? 0) + 1 }));
    timer.current = setTimeout(() => setReaction(null), REACTION_MS);
  };
  return { reaction, react };
}

/** The room's renderer, asked for once the page is live and the browser idle. */
function useLiveScene(
  still: boolean,
  box: React.RefObject<HTMLDivElement | null>,
) {
  const live = useHydrated();
  const [wanted, setWanted] = useState(false);
  const [painted, setPainted] = useState(false);
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    if (!live || still) return;
    const idle =
      window.requestIdleCallback ?? ((run: () => void) => setTimeout(run, 400));
    const handle = idle(() => setWanted(true));
    return () => (window.cancelIdleCallback ?? clearTimeout)(handle);
  }, [live, still]);

  useEffect(() => {
    const node = box.current;
    if (!node || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(([entry]) =>
      setVisible(entry?.isIntersecting ?? true),
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [box]);

  return { wanted, painted, visible, onPainted: () => setPainted(true) };
}

const BAR_BUTTON =
  "inline-grid h-9 min-w-9 place-items-center rounded-xl bg-foreground/8 px-2.5 text-base text-foreground transition hover:bg-foreground/14 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring active:scale-95 aria-pressed:bg-stop/20 aria-pressed:text-stop";

export function LandingRoom({ className }: { className?: string }) {
  const { t } = useTranslation();
  const still = usePrefersReducedMotion();
  const { speaking, line } = useTurns(still);
  const { reaction, react } = useReaction();
  const [muted, setMuted] = useState(false);
  const [raised, setRaised] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  const scene = useLiveScene(still, box);
  const name = (id: string) => t(`landing.people.${id}`);

  const picture: StagePerson[] = [YOU, ...OTHERS].map(
    ({ id, beast, seat }) => ({
      id,
      beast,
      seat,
      name: name(id),
      isYou: id === YOU.id,
      speaking: id === speaking,
      muted: id === MUTED || (id === YOU.id && muted),
      raised: id === YOU.id && raised,
      reaction: id === YOU.id ? reaction?.emoji : undefined,
      message:
        id === line.peerId && line.key % 2 === 0
          ? t(`landing.room.lines.${line.said}`)
          : undefined,
    }),
  );

  return (
    <div
      data-testid="landing-scene"
      className={cn(
        "overflow-hidden rounded-3xl border border-border bg-card shadow-glow-sm",
        className,
      )}
    >
      <div className="flex items-center gap-3 border-b border-border px-3.5 py-2.5 text-xs text-muted-foreground">
        <span aria-hidden="true" className="flex gap-1.5">
          <i className="size-2.5 rounded-full bg-foreground/10" />
          <i className="size-2.5 rounded-full bg-foreground/10" />
          <i className="size-2.5 rounded-full bg-foreground/10" />
        </span>
        <span className="rounded-lg bg-foreground/8 px-2.5 py-1 font-mono text-foreground">
          {t("landing.room.code")}
        </span>
        <span className="ml-auto">
          {t("landing.room.count", { count: OTHERS.length + 1 })}
        </span>
      </div>

      <div ref={box} className="relative h-80 sm:h-[30rem]">
        <div
          aria-hidden={scene.painted || undefined}
          className={cn(
            "absolute inset-0 transition-opacity duration-(--motion-slow)",
            scene.painted && "opacity-0",
          )}
        >
          <NightScope className="h-full">
            <NightStage
              people={picture}
              className="h-full min-h-0 rounded-none border-0"
            />
          </NightScope>
        </div>
        {scene.wanted ? (
          <div
            data-testid="landing-live-scene"
            className={cn(
              "absolute inset-0 transition-opacity duration-(--motion-slow)",
              scene.painted ? "opacity-100" : "pointer-events-none opacity-0",
            )}
          >
            <Suspense fallback={null}>
              <ShowcaseScene
                you={{ name: name(YOU.id), animal: YOU.beast }}
                people={OTHERS.map(({ id, beast, spot }) => ({
                  id,
                  name: name(id),
                  animal: beast,
                  spot,
                  muted: id === MUTED,
                }))}
                muted={muted}
                raised={raised}
                reaction={reaction}
                line={
                  line.key % 2 === 0
                    ? {
                        peerId: line.peerId,
                        text: t(`landing.room.lines.${line.said}`),
                        key: line.key,
                      }
                    : null
                }
                label={t("landing.room.label")}
                visible={scene.visible}
                onFirstFrame={scene.onPainted}
              />
            </Suspense>
          </div>
        ) : null}
      </div>

      <div className="flex items-center gap-1.5 border-t border-border px-3 py-2.5">
        <button
          type="button"
          aria-pressed={muted}
          aria-label={t("landing.room.mute")}
          data-testid="landing-room-mic"
          onClick={() => setMuted((now) => !now)}
          className={BAR_BUTTON}
        >
          {muted ? (
            <MicOff className="size-4" aria-hidden="true" />
          ) : (
            <Mic className="size-4" aria-hidden="true" />
          )}
        </button>
        <span aria-hidden="true" className="mx-1 h-5 w-px bg-border" />
        {REACTIONS.map((emoji) => (
          <button
            key={emoji}
            type="button"
            aria-label={t("landing.room.react", { emoji })}
            data-testid={`landing-room-react-${emoji}`}
            onClick={() => react(emoji)}
            className={BAR_BUTTON}
          >
            {emoji}
          </button>
        ))}
        <button
          type="button"
          aria-pressed={raised}
          aria-label={t("landing.room.hand")}
          data-testid="landing-room-hand"
          onClick={() => setRaised((now) => !now)}
          className={cn(
            BAR_BUTTON,
            "aria-pressed:bg-primary/20 aria-pressed:text-primary",
          )}
        >
          <Hand className="size-4" aria-hidden="true" />
        </button>
        <a
          href="/home"
          className="ml-auto inline-flex h-9 items-center rounded-xl bg-primary px-3.5 text-sm font-semibold text-primary-foreground transition hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {t("landing.room.own")}
        </a>
      </div>
    </div>
  );
}

export default LandingRoom;
